import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import { COLORS, Menu, typographyStyles } from '@livecommerce/design-system';
import Search from '../Search/index.jsx';
import {
  buildRoute,
  ORG_ONE_TO_MANY_HOME,
  ORG_ONE_TO_ONE_HOME,
} from 'constants/routes.js';
import {
  ONE_TO_MANY_PRODUCT,
  ONE_TO_ONE_PRODUCT,
} from 'constants/products.js';
import useProductDataOutsideRoutes from 'hooks/useProductDataOutsideRoutes.js';
import { trackingService } from 'services/tracking/TrackingService.js';
import { useLocalization } from 'contexts/LocalizationContext.jsx';

export const { getBodyText } = typographyStyles;

const MAX_ITEMS_SEEN = 7;
const LIST_ITEM_HEIGHT = 44;
const LOOKAHEAD_PADDING = 15;

const Container = styled.div`
  width: 312px;
  padding: 8px;
  color: ${COLORS.dark_1000};
`;

const OrganizationsListContainer = styled.ul`
  list-style-type: none;
  width: 100%;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  max-height: ${MAX_ITEMS_SEEN * LIST_ITEM_HEIGHT + LOOKAHEAD_PADDING}px;
  min-height: ${(props) => props.minListHeight}px;

  &::after {
    content: '';
    pointer-events: none;
    position: absolute;
    bottom: 8px;
    left: 0;
    right: 0;
    height: ${(props) => props.gradientHeight - 8}px;
    z-index: 0;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.68) 50%,
      rgba(255, 255, 255, 0.84) 100%
    );
  }
`;

const organizationsListStyles = css`
  padding: 0;
`;

const StyledSearch = styled(Search)`
  margin: 8px 10px;
`;

const isGradientShown = (numberOfOrganizations, scrollTop) => {
  const itemsOutOfBounds = numberOfOrganizations - MAX_ITEMS_SEEN;

  return itemsOutOfBounds * LIST_ITEM_HEIGHT - LOOKAHEAD_PADDING !== scrollTop;
};

const getOrganizationRoute = ({ isOneToManyProduct, isOneToOneProduct, orgId, selectedProduct }) => {
  if (selectedProduct === ONE_TO_MANY_PRODUCT && isOneToManyProduct) return buildRoute(ORG_ONE_TO_MANY_HOME, { orgId });
  if (selectedProduct === ONE_TO_ONE_PRODUCT && isOneToOneProduct) return buildRoute(ORG_ONE_TO_ONE_HOME, { orgId });
  if (isOneToManyProduct) return buildRoute(ORG_ONE_TO_MANY_HOME, { orgId });
  if (isOneToOneProduct) return buildRoute(ORG_ONE_TO_ONE_HOME, { orgId });
  return buildRoute(ORG_ONE_TO_MANY_HOME, { orgId }); // todo: revise solution
};

const OrganizationsList = React.forwardRef(({ isOpen, handleMenuItemClick, organizations, ...props }, ref) => {
  const { t } = useLocalization();
  const { selectedProduct } = useProductDataOutsideRoutes();
  const menuRef = useRef(null);
  const [showGradient, setShowGradient] = useState(true);
  const [searchInputValue, setSearchInputValue] = useState('');

  const filteredOrganizations = organizations.filter(({ title, id }) => {
    if (title.toLowerCase().includes(searchInputValue.toLowerCase())) return true;
    if (id.toLowerCase().includes(searchInputValue.toLowerCase())) return true;

    return false;
  });

  useEffect(() => {
    const currentRef = menuRef.current;

    const listener = currentRef?.addEventListener('scroll', (event) => {
      const scrollTop = event.target.scrollTop;
      const gradientIsShown = isGradientShown(filteredOrganizations.length, scrollTop);
      setShowGradient(gradientIsShown);
    });

    return () => {
      currentRef?.removeEventListener('scroll', listener);
    };
  }, [filteredOrganizations.length]);

  const handleChange = (event) => {
    const searchString = event.target.value;
    if (searchString) {
      trackingService.track({
        eventType: 'org-switcher:search',
        value: encodeURIComponent(searchString),
      });
    }
    setSearchInputValue(searchString);
  };

  const gradientHeight =
    showGradient && filteredOrganizations.length > MAX_ITEMS_SEEN ? LIST_ITEM_HEIGHT * 2 + LOOKAHEAD_PADDING : 0;

  const minListHeight =
    organizations.length >= MAX_ITEMS_SEEN ? MAX_ITEMS_SEEN * LIST_ITEM_HEIGHT + LOOKAHEAD_PADDING : 0;

  const filteredOrganizationsItems = filteredOrganizations.map(({ id: orgId, title, features }) => {
    const orgHasOneToManyProduct = features?.shows?.enabled;
    const orgHasOneToOneProduct = features?.calls?.enabled;
    const to = getOrganizationRoute({
      isOneToManyProduct: orgHasOneToManyProduct,
      isOneToOneProduct: orgHasOneToOneProduct,
      orgId,
      selectedProduct,
    });
    return { to, key: orgId, label: title, action: (e) => handleMenuItemClick({ orgId }, e) };
  });

  return (
    <Container ref={ref} {...props}>
      {organizations.length >= MAX_ITEMS_SEEN && (
        <StyledSearch width="140px" placeholder={t('genericLabels.searchPlaceholder')} value={searchInputValue} onChange={handleChange} />
      )}
      <OrganizationsListContainer ref={menuRef} gradientHeight={gradientHeight} minListHeight={minListHeight}>
        <Menu items={filteredOrganizationsItems} minWidth={288} className={organizationsListStyles} />
      </OrganizationsListContainer>
    </Container>
  );
});

export default OrganizationsList;
